import React, { useMemo, useRef } from "react";
import { format, parseISO, addDays, differenceInCalendarDays } from "date-fns";
import { mdy, md } from "@/lib/dateFmt";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { colorForShift, shiftLabel, COLOR_MAP } from "@/lib/shifts";
import { StickyNote, RotateCcw, AlertCircle, CheckCircle2, AlertTriangle } from "lucide-react";
import LevelBadge from "@/components/LevelBadge";

// Grid template is now derived from the day count so months (28–31 columns)
// scroll horizontally without breaking min-widths.
function buildGridCols(n) {
  return `220px repeat(${n}, minmax(64px, 1fr)) 72px`;
}
function buildGridColsLg(n) {
  return `lg:[grid-template-columns:300px_repeat(${n},minmax(70px,1fr))_84px]`;
}

// Farmer name cell polish based on their farm level.
// Legendary (L6) → gold gradient + gold ring; Harvest Master (L5) → subtle amber tint.
function nameCellFlourish(lvl) {
  const n = lvl?.level || 0;
  if (n >= 6) return {
    wrap: "bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-100 ring-1 ring-amber-400 shadow-sm",
    name: "text-amber-900 font-semibold",
  };
  if (n >= 5) return {
    wrap: "bg-gradient-to-r from-orange-50 to-amber-50 ring-1 ring-orange-200",
    name: "text-orange-900 font-semibold",
  };
  return { wrap: "", name: "text-slate-900" };
}

export default function ScheduleGrid({
  start, end, employees, roster, assignments, coverageShifts = [], availabilityByEmployee = {},
  pendingOffers = [], standaloneNotesByEmployee = {}, nsoByEmployee = {}, adjacentPeek = { by_employee: {} },
  codes, canEdit,
  onCellClick, onEmployeeClick, onResetEmployee,
  openDays, onCellReveal, onCoverageClick, onCoverageCellClick,
  onOpenChipClick, onDayHeaderClick,
  levelMap = {},
  dividers = [],
}) {
  const startDate = useMemo(() => parseISO(start), [start]);
  const endDate = useMemo(() => (end ? parseISO(end) : addDays(startDate, 13)), [end, startDate]);
  const days = useMemo(() => {
    const n = Math.max(1, differenceInCalendarDays(endDate, startDate) + 1);
    return Array.from({ length: n }, (_, i) => addDays(startDate, i));
  }, [startDate, endDate]);
  const codeMap = useMemo(() => new Map((codes || []).map((c) => [c.code, c])), [codes]);
  const todayIso = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);
  // Map (employee_id + '|' + date) → array of pending offers for that recipient/cell.
  const pendingOffersByCell = useMemo(() => {
    const m = new Map();
    for (const o of pendingOffers) {
      const k = `${o.employee_id}|${o.date}`;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(o);
    }
    return m;
  }, [pendingOffers]);
  // Standalone notes (cells without an assignment but with a manager note).
  const standaloneNoteByCell = useMemo(() => {
    const m = new Map();
    Object.entries(standaloneNotesByEmployee || {}).forEach(([eid, arr]) => {
      (arr || []).forEach(n => m.set(`${eid}|${n.date}`, n));
    });
    return m;
  }, [standaloneNotesByEmployee]);
  // NSO requests per (employee, date, code) — chip surface on cells for
  // OT-review + bumped requests so managers see them at a glance.
  const nsoByCell = useMemo(() => {
    const m = new Map();
    Object.entries(nsoByEmployee || {}).forEach(([eid, arr]) => {
      (arr || []).forEach(r => {
        const k = `${eid}|${r.date}`;
        if (!m.has(k)) m.set(k, []);
        m.get(k).push(r);
      });
    });
    return m;
  }, [nsoByEmployee]);
  const dividerByIso = useMemo(() => {
    const m = new Map();
    (dividers || []).forEach((d) => m.set(d.date, d));
    return m;
  }, [dividers]);

  const gridCols = useMemo(() => buildGridCols(days.length), [days.length]);
  const gridColsLg = useMemo(() => buildGridColsLg(days.length), [days.length]);

  // Cross-hair hover: light up the employee name (left, sticky) and the date
  // header (top) whenever the mouse is over a cell in that row/column. Done
  // via refs + direct classList toggle so the whole grid doesn't re-render
  // on every cell hover (which was making it feel laggy). Soft slate-100
  // tint, no border — deliberately understated.
  const headerRefs = useRef({});   // iso -> DOM element
  const nameRefs = useRef({});     // empId -> DOM element
  const activeHeader = useRef(null);
  const activeName = useRef(null);
  const HOVER_CLASS = "xhair-hi";
  const setHoverTarget = (iso, empId) => {
    const h = iso ? headerRefs.current[iso] : null;
    const n = empId ? nameRefs.current[empId] : null;
    if (activeHeader.current && activeHeader.current !== h) activeHeader.current.classList.remove(HOVER_CLASS);
    if (activeName.current && activeName.current !== n) activeName.current.classList.remove(HOVER_CLASS);
    if (h) h.classList.add(HOVER_CLASS);
    if (n) n.classList.add(HOVER_CLASS);
    activeHeader.current = h || null;
    activeName.current = n || null;
  };
  const clearHoverTarget = () => setHoverTarget(null, null);
  const minWrapperPx = 220 + days.length * 64 + 72; // manual sanity min-w

  const assignMap = useMemo(() => {
    const m = new Map();
    for (const a of assignments) m.set(`${a.employee_id}|${a.date}`, a);
    return m;
  }, [assignments]);

  const coverageMap = useMemo(() => {
    const m = new Map();
    for (const c of coverageShifts) {
      const key = `${c.employee_id}|${c.date}`;
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(c);
    }
    return m;
  }, [coverageShifts]);

  // Recompute paid hours from stored times (30-min lunch deduction when raw > 6h).
  // Falls back to the shift-code's default start/end if the assignment doesn't
  // carry its own times yet (legacy rows).
  const paidHours = (a) => {
    if (!a) return 0;
    let s = a.start_time, e = a.end_time;
    if (!s || !e) {
      const sh = codeMap.get(a.shift_code || a.code);
      if (sh?.start && sh?.end) { s = sh.start; e = sh.end; }
    }
    if (s && e) {
      const [sh_, sm] = s.split(":").map(Number);
      const [eh, em] = e.split(":").map(Number);
      let mins = (eh * 60 + em) - (sh_ * 60 + sm);
      if (mins < 0) mins += 24 * 60;
      let h = mins / 60;
      if (h > 6) h -= 0.5;
      return Math.round(h * 100) / 100;
    }
    return a.hours || 0;
  };

  const totalHoursFor = (empId) =>
    days.reduce((sum, d) => {
      const iso = format(d, "yyyy-MM-dd");
      const a = assignMap.get(`${empId}|${iso}`);
      const covs = coverageMap.get(`${empId}|${iso}`) || [];
      const covHours = covs.reduce((s, c) => s + paidHours(c), 0);
      const shiftHours = (!a || a.external) ? 0 : paidHours(a);
      return sum + shiftHours + covHours;
    }, 0);

  const totalOpen = (openDays || []).reduce(
    (s, d) => s + (d?.open?.length || 0) + (d?.coverage?.length || 0), 0
  );

  return (
    <TooltipProvider delayDuration={200}>
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        {/* Single scroll container: both directions live here so the sticky header
            attaches to this box. On mobile we take almost the whole viewport
            height so only one scroller ever moves (the grid itself) — no more
            "two scrollbars, can't reach the bottom row" trap.  On desktop we
            keep the older ceiling so surrounding chrome stays visible. */}
        <div className="grid-scroll-container overflow-auto thin-scroll max-h-[calc(100dvh-160px)] md:max-h-[calc(100vh-220px)] min-h-[380px] md:min-h-[420px]" data-testid="grid-scroll">
          <div className={`[grid-template-columns:${gridCols}]`}
               style={{ minWidth: `${minWrapperPx}px` }}>
            {/* Sticky top: open shifts + day header scroll together */}
            <div className="sticky top-0 z-30 bg-white shadow-[0_2px_6px_-4px_rgba(15,23,42,0.15)]">
            {/* Open shifts row */}
            {openDays && openDays.length > 0 && (
              <div
                className={`grid border-b border-slate-200 bg-amber-50 ${gridColsLg}`}
                style={{ gridTemplateColumns: gridCols }}
                data-testid="grid-open-shifts"
              >
                <div className="sticky left-0 z-20 bg-amber-50 border-r border-amber-200/40 px-2 py-2 flex items-center gap-1.5 text-xs font-medium text-slate-700">
                  {totalOpen > 0
                    ? <AlertCircle size={12} className="text-amber-600" />
                    : <CheckCircle2 size={12} className="text-emerald-600" />}
                  <span className="hidden sm:inline">Open shifts</span>
                  <span className="sm:hidden">Open</span>
                </div>
                {days.map((d, i) => {
                  const day = openDays[i];
                  const openList = day?.open || [];
                  const covList = day?.coverage || [];
                  const isWeekend = [0, 6].includes(d.getDay());
                  const iso = format(d, "yyyy-MM-dd");
                  const isToday = iso === todayIso;
                  const dv = dividerByIso.get(iso);
                  return (
                    <div
                      key={i}
                      onClick={(e) => {
                        // If the user clicked empty space (not on a chip), open
                        // the Day Coverage dialog anyway so managers can add /
                        // manage shifts for that date even when nothing is open.
                        if (onOpenChipClick && !e.target.closest("button")) {
                          onOpenChipClick(iso, null, null);
                        }
                      }}
                      className={`border-l border-slate-100 min-h-[52px] p-1 flex flex-wrap gap-1 justify-center items-center ${onOpenChipClick ? "cursor-pointer hover:bg-amber-100" : ""} ${isWeekend ? "col-weekend" : "bg-amber-50"} ${isToday ? "border-l-2 border-r-2 border-blue-500 bg-blue-50" : ""} ${dv ? "!border-l-[3px] !border-l-slate-600" : ""}`}
                      data-testid={`open-col-${iso}`}
                    >
                      {openList.length === 0 && onOpenChipClick && (
                        <span className="text-[10px] text-slate-300 group-hover:text-slate-400">·</span>
                      )}
                      {openList.map((code, j) => {
                        const s = codeMap.get(code);
                        const clr = colorForShift(s || { code, color: "open_shift" });
                        const iso = format(d, "yyyy-MM-dd");
                        const isPast = iso < format(new Date(), "yyyy-MM-dd");
                        const clickable = !!onOpenChipClick && !isPast;
                        // Manager visibility: if there's a pending offer out for this
                        // (date, code), render the chip in green with an "OFFER OUT" ribbon.
                        const offerMeta = (day?.offered || []).find(o => o.code === code);
                        const offered = !!offerMeta;
                        return (
                          <Tooltip key={`o-${j}`}>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                onClick={() => clickable && onOpenChipClick(iso, code, s)}
                                disabled={!clickable}
                                data-testid={`open-chip-${iso}-${j}`}
                                className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-semibold
                                            ${offered
                                              ? "bg-emerald-100 text-emerald-800 border-emerald-400 ring-1 ring-emerald-300"
                                              : `${clr.bg} ${clr.text} ${clr.border}`}
                                            ${clickable ? "hover:brightness-95 hover:shadow-sm cursor-pointer" : "cursor-help"}
                                            ${isPast && onOpenChipClick ? "opacity-60" : ""}`}
                              >
                                {code}
                                {offered && (
                                  <span className="inline-flex items-center gap-0.5 rounded-sm bg-emerald-600 text-white px-1 text-[9px] font-bold uppercase tracking-wider tabular-nums" data-testid={`offer-badge-${iso}-${code}`}>
                                    · {offerMeta.pending_count}
                                  </span>
                                )}
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-xs max-w-[240px]">
                              {offered ? (
                                <>
                                  <div className="font-medium">Offer out · awaiting response</div>
                                  <div className="text-[10px] text-emerald-200 mt-0.5">
                                    {offerMeta.pending_count} of {offerMeta.total_count} still pending
                                    {offerMeta.sent_by_name && ` · sent by ${offerMeta.sent_by_name}`}
                                  </div>
                                  <div className="text-[10px] text-blue-200 mt-1">Click to manage or re-offer</div>
                                </>
                              ) : (
                                <>
                                  Open · {s ? shiftLabel(s) : code}
                                  {clickable && <div className="text-blue-200 mt-0.5 text-[10px]">Click to request pickup</div>}
                                  {isPast && onOpenChipClick && <div className="text-slate-300 mt-0.5 text-[10px]">Past date — can&apos;t pick up</div>}
                                </>
                              )}
                            </TooltipContent>
                          </Tooltip>
                        );
                      })}
                      {covList.map((cov, j) => {
                        // covList is now array of objects: {code, assignment_id, source_employee_name, gap_start, gap_end, gap_hours}
                        const covCode = typeof cov === "string" ? cov : cov.code;
                        const covMeta = typeof cov === "string" ? null : cov;
                        const gapLabel = covMeta && covMeta.gap_start && covMeta.gap_end
                          ? `${covMeta.gap_start}–${covMeta.gap_end} (${covMeta.gap_hours}h)`
                          : "";
                        return (
                          <Tooltip key={`c-${j}`}>
                            <TooltipTrigger asChild>
                              <button
                                onClick={() => canEdit && covMeta && onCoverageClick?.(covMeta, day?.date)}
                                disabled={!canEdit || !covMeta}
                                data-testid={`cov-chip-${format(d, "yyyy-MM-dd")}-${j}`}
                                className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[11px] font-semibold bg-pink-100 text-pink-800 border-pink-300
                                            ${canEdit && covMeta ? "hover:bg-pink-200 hover:border-pink-400 cursor-pointer" : "cursor-help"}`}
                              >
                                {covCode}Cvg
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-xs max-w-[240px]">
                              <div className="font-medium">Coverage needed</div>
                              {gapLabel && <div className="text-slate-100 mt-0.5">{gapLabel}</div>}
                              {covMeta?.source_employee_name && (
                                <div className="text-slate-300 mt-0.5">Original: {covMeta.source_employee_name}</div>
                              )}
                              {canEdit && <div className="text-pink-200 mt-1 text-[10px]">Click to assign a coverer</div>}
                            </TooltipContent>
                          </Tooltip>
                        );
                      })}
                      {openList.length === 0 && covList.length === 0 && (
                        <span className="text-[11px] text-slate-300">—</span>
                      )}
                    </div>
                  );
                })}
                <div className="border-l border-slate-100 bg-amber-50 p-2 text-center text-[11px] tabular-nums text-slate-700 font-medium flex items-center justify-center">
                  {totalOpen}
                </div>
              </div>
            )}

            {/* Header row */}
            <div
              className={`grid bg-white border-b border-slate-200 ${gridColsLg}`}
              style={{ gridTemplateColumns: gridCols }}
            >
              <div className="sticky left-0 z-20 bg-white border-r border-slate-100 p-2 lg:p-3 text-[10px] lg:text-xs uppercase tracking-widest text-slate-500 font-medium">
                <span className="hidden sm:inline">Employee · FTE</span>
                <span className="sm:hidden">Employee</span>
              </div>
              {days.map((d) => {
                const isWeekend = [0, 6].includes(d.getDay());
                const iso = format(d, "yyyy-MM-dd");
                const isToday = iso === todayIso;
                const dv = dividerByIso.get(iso);
                return (
                  <button
                    type="button"
                    key={d.toISOString()}
                    ref={(el) => { if (el) headerRefs.current[iso] = el; else delete headerRefs.current[iso]; }}
                    onClick={() => canEdit && onDayHeaderClick?.(iso)}
                    data-testid={`day-header-${iso}`}
                    title={canEdit ? (isToday ? "Today — 24-hour headcount view" : "Click for a 24-hour headcount view") : ""}
                    className={`p-1.5 lg:p-2 text-center border-l border-slate-100 bg-white ${canEdit ? "hover:bg-blue-50 focus:bg-blue-50 cursor-pointer" : "cursor-default"} focus:outline-none transition-colors relative ${isWeekend ? "col-weekend" : ""} ${isToday ? "col-today-header" : ""} ${dv ? "!border-l-[3px] !border-l-slate-600" : ""}`}
                  >
                    {dv && (
                      <div
                        className="absolute -top-0 -left-0.5 z-40 bg-slate-700 text-white text-[8px] font-black uppercase tracking-widest px-1 py-0.5 rounded-br-md shadow-sm whitespace-nowrap"
                        data-testid={`pp-divider-${iso}`}
                        title={dv.label}
                      >
                        {dv.label}
                      </div>
                    )}
                    {isToday && (
                      <div className="absolute -top-0 left-1/2 -translate-x-1/2 -translate-y-0 z-30 bg-blue-600 text-white text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-b-md shadow-sm" data-testid={`today-banner-${iso}`}>
                        Today
                      </div>
                    )}
                    <div className={`text-[9px] lg:text-[10px] uppercase tracking-widest ${isToday ? "text-blue-700 font-semibold pt-2" : "text-slate-400"}`}>
                      {format(d, "EEE")}
                    </div>
                    <div className={`text-xs lg:text-sm font-display font-semibold tabular-nums ${isToday ? "text-blue-800" : "text-slate-800"}`}>
                      {md(d)}
                    </div>
                  </button>
                );
              })}
              <div className="p-2 text-center border-l border-slate-100 bg-white text-[10px] uppercase tracking-widest text-slate-500">
                Hours
              </div>
            </div>
            </div>{/* end sticky top */}

            {/* Rows */}
            {(roster && roster.length ? roster : employees.map((e) => ({ ...e, type: "employee" }))).map((it) => {
              if (it.type === "separator") {
                return (
                  <div
                    key={`sep-${it.id}`}
                    className="border-b border-slate-100 bg-slate-50 px-3 py-1.5 flex items-center gap-3 sticky left-0"
                    data-testid={`grid-sep-${it.id}`}
                  >
                    <div className="h-px bg-slate-300 flex-1" />
                    <span className="text-[11px] uppercase tracking-widest text-slate-600 font-semibold">{it.label}</span>
                    <div className="h-px bg-slate-300 flex-1" />
                  </div>
                );
              }
              const emp = it;
              return (
                <React.Fragment key={emp.id}>
                <div
                  className={`grid border-b border-slate-100 hover:bg-slate-50/40 transition-colors group ${gridColsLg}`}
                  style={{ gridTemplateColumns: gridCols }}
                  data-testid={`row-${emp.id}`}
                >
                  <div
                    ref={(el) => { if (el) nameRefs.current[emp.id] = el; else delete nameRefs.current[emp.id]; }}
                    className="sticky left-0 z-10 bg-white group-hover:bg-slate-50 border-r border-slate-100 flex items-center gap-2 p-2 min-w-0 shadow-[6px_0_10px_-8px_rgba(15,23,42,0.15)] transition-colors">
                    <button
                      onClick={() => onEmployeeClick?.(emp)}
                      data-testid={`employee-${emp.id}`}
                      className={`flex items-center gap-2 lg:gap-3 text-left rounded-lg p-1.5 flex-1 min-w-0 transition-colors ${nameCellFlourish(levelMap[emp.id]).wrap || "hover:bg-slate-100/70"}`}
                    >
                      <Avatar className="w-8 h-8 lg:w-9 lg:h-9 shrink-0">
                        <AvatarImage src={emp.avatar} />
                        <AvatarFallback>{emp.name?.[0]}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className={`text-sm lg:text-[15px] leading-tight break-words ${nameCellFlourish(levelMap[emp.id]).name}`} data-testid={`emp-name-${emp.id}`}>
                          {emp.name}
                        </div>
                        <div className="text-[10px] lg:text-[11px] text-slate-500 truncate flex items-center gap-1.5 mt-0.5">
                          {levelMap[emp.id]?.level > 0 ? (
                            <LevelBadge level={levelMap[emp.id]} size="sm" showLabel />
                          ) : (
                            <span className="truncate">{emp.role_label || emp.position}</span>
                          )}
                          {typeof emp.fte === "number" && (
                            <span className="tabular-nums shrink-0 rounded-md bg-slate-100 text-slate-700 px-1.5 py-0.5 text-[9px] lg:text-[10px] font-semibold">
                              FTE {emp.fte.toFixed(1)}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                    {canEdit && onResetEmployee && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => onResetEmployee(emp)}
                            data-testid={`emp-reset-${emp.id}`}
                            className="shrink-0 text-slate-400 hover:text-blue-600 transition-colors p-1 hidden sm:block"
                            aria-label="Reset to master"
                          >
                            <RotateCcw size={14} />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="text-xs">
                          Apply {emp.name?.split(" ")[0]}&apos;s master pattern to this pay period
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>

                  {days.map((d, dayIdx) => {
                    const iso = format(d, "yyyy-MM-dd");
                    const a = assignMap.get(`${emp.id}|${iso}`);
                    const covs = coverageMap.get(`${emp.id}|${iso}`) || [];
                    const isExternal = !!a?.external;
                    const isModified = !isExternal && (a?.is_modified || a?.hours_override != null);
                    // Adjacent pay-period peek — first (dayIdx=0) and last (last col)
                    // cells surface a tiny popover with the neighbour-day assignment
                    // so managers spot cross-boundary consecutive-days risk.
                    const isFirstCol = dayIdx === 0;
                    const isLastCol  = dayIdx === days.length - 1;
                    const peek = (isFirstCol || isLastCol) ? (adjacentPeek.by_employee?.[emp.id] || {}) : {};
                    const peekEntry = isFirstCol ? peek.prev : (isLastCol ? peek.next : null);
                    // Recipient has an OPEN pending offer for this date? Even if
                    // they don't have an assignment yet, paint the cell light
                    // green and show a small OFFER badge.
                    const cellOffers = pendingOffersByCell.get(`${emp.id}|${iso}`) || [];
                    const hasPendingOfferForCell = cellOffers.length > 0;
                    const nsoReqs = nsoByCell.get(`${emp.id}|${iso}`) || [];
                    const nsoOtReview = nsoReqs.find(r => r.status === "ot_review" || (r.status === "pending" && r.wants_ot));
                    const nsoBumped = nsoReqs.find(r => r.status === "bumped");
                    const nsoPending = nsoReqs.find(r => r.status === "pending" && !r.wants_ot);
                    // PTO pending — the underlying assignment carries code "P"
                    // plus the employee's ORIGINAL code in prev_shift_code so
                    // the cell still shows what needs coverage.
                    const isPtoPending = !isExternal && a?.shift_code === "P" && !!a?.prev_shift_code;
                    const isPtoPendingOff = !isExternal && a?.shift_code === "U" && !!a?.pto_pending;
                    const isAnyPtoPending = isPtoPending || isPtoPendingOff;
                    // Any approved hour modification (arrive late / leave
                    // early / long lunch) or a left-sick-early partial
                    // absence — paint the cell #FFCCFF so it stands out
                    // as a "hours were adjusted today" marker.
                    const isHourMod = !isExternal && !!a && (
                      !!a.modification_request_id
                      || !!a.long_lunch_note
                      || a.absent_type === "left_sick_early"
                    );
                    const isOffered = !isExternal && (!!a?.has_pending_offer || hasPendingOfferForCell);
                    const isOTFlag = !isExternal && !!a?.ot_violation;
                    // Left-sick-early: kept as an Absent row (rose paint) but we
                    // display the ORIGINAL shift code + a "Left @ HH:MM" pill so
                    // managers can see the person worked part of the shift.
                    const isLeftEarly = !isExternal && a?.absent_type === "left_sick_early" && !!a?.original_shift_code;
                    const stackedExtras = (a?.extra_shifts && a.extra_shifts.length) ? a.extra_shifts : null;
                    const stackedCodes = stackedExtras
                      ? [a.shift_code, ...stackedExtras.map(e => e.shift_code)].join(", ")
                      : null;
                    // Training highlight: if 'Tr' is anywhere in the cell —
                    // primary or extras — paint the whole cell orange so the
                    // trainer/trainee are visually distinct at a glance.
                    const hasTraining = !isExternal && a && (
                      a.shift_code === "Tr" ||
                      (stackedExtras && stackedExtras.some(e => e.shift_code === "Tr"))
                    );
                    const isTrainer = !isExternal && !!(a?.trainer_of && a.trainer_of.length);
                    const trainingDisplay = (a?.shift_code === "Tr" && a?.training_of_code)
                      ? `${a.training_of_code}Tr`
                      : null;
                    const displayCode = trainingDisplay ? trainingDisplay
                                        : stackedCodes ? stackedCodes
                                        : isPtoPending ? a.prev_shift_code
                                        : isLeftEarly ? a.original_shift_code
                                        : a?.shift_code;
                    const primaryShift = a && !isExternal
                      ? codeMap.get(isPtoPending ? a.prev_shift_code : a.shift_code)
                      : null;
                    // Synthesise a "training" shift descriptor whenever Tr is
                    // present so `colorForShift` returns the orange palette.
                    const shift = (hasTraining || isTrainer)
                      ? { ...(primaryShift || {}), color: "training_orange", type: "work" }
                      : primaryShift;
                    const baseColor = a && !isExternal
                      ? (isAnyPtoPending ? COLOR_MAP.pending_magenta
                         : isHourMod  ? COLOR_MAP.mod_pink
                         : isOffered ? COLOR_MAP.offered_green
                         : isOTFlag  ? COLOR_MAP.ot_red
                         : colorForShift(shift || { code: a.shift_code, color: "regular" }))
                      : COLOR_MAP.empty;
                    // isHourMod (approved arrive_late / leave_early / long
                    // lunch / left-sick-early) wins over the older generic
                    // coverage_pink treatment so the whole "hours were
                    // adjusted today" family lands on the same #FFCCFF.
                    const c = isHourMod ? COLOR_MAP.mod_pink
                             : isModified ? COLOR_MAP.coverage_pink
                             : baseColor;
                    const isWeekend = [0, 6].includes(d.getDay());
                    const isToday = iso === todayIso;
                    const hasNote = !isExternal && !!(a?.has_note || a?.note || (a?.notes && a.notes.length));
                    const noteCount = a?.notes?.length || 0;
                    // Meeting note = distinct colored outline on the cell so
                    // staff can see at a glance that they've got a meeting slot
                    // during this shift. Uses the session color the backend
                    // stamped on each shift_notes doc when finalize ran.
                    const meetingNote = (a?.notes || []).find(n => (n.kind || "").startsWith("meeting_"));
                    const meetingRingStyle = meetingNote
                      ? { boxShadow: `inset 0 0 0 2px ${meetingNote.color || "#2563eb"}` }
                      : null;

                    const extLabel = isExternal
                      ? (a.external_facility_label || a.external_facility_id || "OTHER").slice(0, 3).toUpperCase()
                      : null;

                    const handleClick = () => {
                      if (canEdit && !isExternal) onCellClick?.(emp, iso, a);
                      else if (a) onCellReveal?.(emp, iso, a);
                    };

                    const cellButton = isExternal ? (
                      <button
                        onClick={handleClick}
                        data-testid={`cell-${emp.id}-${iso}`}
                        data-external={a.external_facility_id}
                        style={meetingRingStyle || undefined}
                        className="cell-base relative m-1 h-12 lg:h-14 rounded-lg border w-[calc(100%-8px)]
                                   border-dashed border-slate-300 bg-slate-50 text-slate-500
                                   text-[10px] lg:text-[11px] font-semibold flex items-center justify-center cursor-help
                                   bg-[repeating-linear-gradient(45deg,#f8fafc,#f8fafc_6px,#eef2f7_6px,#eef2f7_12px)]"
                      >
                        {extLabel}
                      </button>
                    ) : a ? (
                      <button
                        onClick={handleClick}
                        data-testid={`cell-${emp.id}-${iso}`}
                        style={meetingRingStyle || undefined}
                        title={
                          meetingNote
                            ? [
                                meetingNote.kind === "meeting_cover" ? "Covering meeting" : "Attending meeting",
                                (meetingNote.meeting_start && meetingNote.meeting_end)
                                  ? `${meetingNote.meeting_start}–${meetingNote.meeting_end}`
                                  : null,
                                meetingNote.content,
                              ].filter(Boolean).join(" · ")
                            : undefined
                        }
                        className={`cell-base relative m-1 h-12 lg:h-14 rounded-lg border w-[calc(100%-8px)] ${c.bg} ${c.text} ${c.border}
                                    text-xs lg:text-sm font-semibold flex flex-col items-center justify-center leading-tight
                                    ${canEdit ? "cursor-pointer hover:shadow-sm hover:border-blue-400" : "cursor-help"}
                                    ${isWeekend && !a ? "bg-slate-50" : ""}
                                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1`}
                      >
                        <span>{displayCode}</span>
                        <span className="text-[9px] font-medium opacity-80 tabular-nums lg:hidden">
                          {a.hours}h
                        </span>
                        {isModified && (
                          <span className="hidden lg:inline text-[10px] font-medium opacity-90 tabular-nums">
                            {a.hours}h
                          </span>
                        )}
                        {isLeftEarly && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                data-testid={`cell-lse-badge-${emp.id}-${iso}`}
                                onClick={(e) => e.stopPropagation()}
                                className="absolute -bottom-1 left-1 right-1 rounded-md bg-rose-100 text-rose-800 border border-rose-300 text-[8px] lg:text-[9px] font-bold px-1 py-0.5 leading-none tabular-nums shadow-sm"
                              >
                                Left @ {a.left_at} · {a.hours}h
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-xs max-w-[240px]">
                              <div className="font-medium">Left sick early</div>
                              <div className="text-[11px] text-slate-100 mt-0.5">
                                Worked {a.original_start_time}–{a.left_at} ({a.hours}h). Scheduled to end {a.original_end_time}.
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        )}
                        {!!(a?.ot_hours) && a.ot_hours > 0 && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                data-testid={`cell-ot-badge-${emp.id}-${iso}`}
                                onClick={(e) => e.stopPropagation()}
                                className="absolute -bottom-1 left-1 rounded-md bg-red-600 text-white text-[8px] lg:text-[9px] font-bold px-1.5 py-0.5 leading-none tabular-nums shadow-sm ring-1 ring-white/70"
                              >
                                OT {a.ot_hours}h
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-xs max-w-[240px]">
                              <div className="font-medium">Overtime hours recorded</div>
                              <div className="text-[11px] text-slate-100 mt-0.5">
                                {a.ot_hours}h of {a.hours}h on this cell are logged as OT.
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        )}
                        {peekEntry && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`cell-peek-${emp.id}-${iso}`}
                                className={`absolute ${isFirstCol ? "top-0.5 left-0.5" : "top-0.5 right-0.5"} rounded-md bg-sky-600 text-white text-[8px] lg:text-[9px] font-bold px-1 py-0.5 leading-none shadow-sm ring-1 ring-white/70`}
                              >
                                {isFirstCol ? "‹" : "›"}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side={isFirstCol ? "left" : "right"} className="text-xs max-w-[240px]">
                              <div className="font-semibold text-white">
                                {isFirstCol ? "Prior pay period" : "Next pay period"}
                              </div>
                              <div className="text-[11px] text-slate-100 mt-0.5">
                                {peekEntry.date} · <span className="font-mono">{peekEntry.shift_code}</span>
                                {peekEntry.hours ? ` · ${peekEntry.hours}h` : ""}
                              </div>
                              {peekEntry.start_time && peekEntry.end_time && (
                                <div className="text-[11px] text-slate-100 mt-0.5">
                                  {peekEntry.start_time} – {peekEntry.end_time}
                                </div>
                              )}
                            </TooltipContent>
                          </Tooltip>
                        )}
                        {a?.traded_from_employee_name && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                data-testid={`cell-traded-badge-${emp.id}-${iso}`}
                                onClick={(e) => e.stopPropagation()}
                                className="absolute top-0.5 right-0.5 rounded-md bg-violet-500 text-white text-[8px] lg:text-[9px] font-bold px-1 py-0.5 leading-none shadow-sm ring-1 ring-white/70 flex items-center gap-0.5"
                              >
                                ⇄
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-xs max-w-[240px]">
                              <div className="font-medium">Traded shift</div>
                              <div className="text-[11px] text-slate-100 mt-0.5">
                                From {a.traded_from_employee_name}
                                {a.traded_at ? ` · ${mdy(a.traded_at)}` : ""}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        )}
                        {(nsoOtReview || nsoBumped || nsoPending) && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                data-testid={`cell-nso-badge-${emp.id}-${iso}`}
                                onClick={(e) => e.stopPropagation()}
                                className={`absolute bottom-0.5 right-0.5 rounded-md text-white text-[8px] lg:text-[9px] font-bold px-1 py-0.5 leading-none shadow-sm ring-1 ring-white/70 ${
                                  nsoOtReview ? "bg-orange-500"
                                  : nsoBumped ? "bg-slate-500"
                                  : "bg-indigo-500"
                                }`}
                              >
                                {nsoOtReview ? "OT?" : nsoBumped ? "req" : "NSO"}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-xs max-w-[260px]">
                              <div className="font-medium">
                                {nsoOtReview ? "OT-review request"
                                  : nsoBumped ? "Not assigned"
                                  : "Pending NSO request"}
                              </div>
                              <ul className="text-[11px] text-slate-100 mt-1 space-y-0.5">
                                {nsoReqs.map((r, i) => (
                                  <li key={i}>
                                    <span className="font-mono">{r.shift_code}</span>
                                    {r.wants_ot ? " · wants OT" : ""}
                                    {r.rejection_reason ? ` · ${r.rejection_reason}` : ""}
                                  </li>
                                ))}
                              </ul>
                            </TooltipContent>
                          </Tooltip>
                        )}
                        {isTrainer && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                data-testid={`cell-trainer-badge-${emp.id}-${iso}`}
                                onClick={(e) => e.stopPropagation()}
                                className="absolute top-0.5 left-0.5 rounded-md bg-orange-500 text-white text-[8px] lg:text-[9px] font-bold px-1 py-0.5 leading-none shadow-sm ring-1 ring-white/70 tabular-nums"
                              >
                                Tr{a.trainer_of.length > 1 ? ` ×${a.trainer_of.length}` : ""}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-xs max-w-[260px]">
                              <div className="font-semibold mb-1 flex items-center gap-1">
                                <span className="font-mono px-1 rounded bg-orange-400 text-white">Tr</span> Training today
                              </div>
                              <ul className="space-y-0.5 list-disc pl-4 text-[11px] text-slate-100">
                                {a.trainer_of.map((t, i) => (
                                  <li key={i}>{t.trainee_name} on the {t.code} shift</li>
                                ))}
                              </ul>
                            </TooltipContent>
                          </Tooltip>
                        )}
                        {covs.length > 0 && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                data-testid={`cell-cov-badge-${emp.id}-${iso}`}
                                onClick={(e) => { e.stopPropagation(); if (canEdit) onCoverageCellClick?.(covs[0]); }}
                                role="button"
                                tabIndex={0}
                                className={`absolute -bottom-1 left-1 right-1 rounded-md bg-pink-100 text-pink-800 border border-pink-300 text-[8px] lg:text-[9px] font-bold px-1 py-0.5 leading-none tabular-nums shadow-sm ${canEdit ? "cursor-pointer hover:bg-pink-200" : ""}`}
                              >
                                +{covs.reduce((s, cv) => s + (cv.hours || 0), 0)}h Cov
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-xs max-w-[240px]">
                              <div className="font-medium">
                                Covering {covs[0].source_employee_name}
                              </div>
                              <div className="text-[11px] text-slate-100 mt-0.5">
                                {covs[0].code} · {covs[0].start_time && covs[0].end_time ? `${covs[0].start_time}–${covs[0].end_time} · ` : ""}{covs.reduce((s, cv) => s + (cv.hours || 0), 0)}h
                              </div>
                              {canEdit && <div className="text-[10px] text-pink-200 mt-1">Click to manage / clear</div>}
                            </TooltipContent>
                          </Tooltip>
                        )}
                        {a?.long_lunch_note && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                data-testid={`cell-ll-badge-${emp.id}-${iso}`}
                                className="absolute -bottom-1 left-1 right-1 rounded-md bg-orange-100 text-orange-800 border border-orange-300 text-[8px] lg:text-[9px] font-bold px-1 py-0.5 leading-none tabular-nums shadow-sm"
                              >
                                LL {a.long_lunch_start}–{a.long_lunch_end}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-xs max-w-[240px]">
                              <div className="font-medium">Long lunch</div>
                              <div className="text-[11px] text-slate-100 mt-0.5">{a.long_lunch_note}</div>
                            </TooltipContent>
                          </Tooltip>
                        )}
                        {hasNote && (
                          <span
                            data-testid={`cell-note-${emp.id}-${iso}`}
                            className={`absolute top-0.5 min-w-[14px] h-[14px] px-1 rounded-full bg-amber-500 text-white text-[9px] font-bold grid place-items-center shadow-sm leading-none tabular-nums ${(a?.cautions?.length || 0) > 0 ? "right-[18px]" : "right-0.5"}`}
                            aria-label={`${noteCount} note${noteCount === 1 ? "" : "s"}`}
                          >
                            {noteCount > 1 ? noteCount : <StickyNote size={8} strokeWidth={3} />}
                          </span>
                        )}
                        {meetingNote && (
                          <span
                            data-testid={`cell-meeting-${emp.id}-${iso}`}
                            className="absolute bottom-0 left-1 right-1 h-1 rounded-full"
                            style={{ background: meetingNote.color || "#2563eb" }}
                            aria-label={meetingNote.kind === "meeting_cover" ? "Meeting cover" : "Meeting attendee"}
                            title={
                              [
                                meetingNote.kind === "meeting_cover" ? "Covering meeting" : "Attending meeting",
                                (meetingNote.meeting_start && meetingNote.meeting_end)
                                  ? `${meetingNote.meeting_start}–${meetingNote.meeting_end}`
                                  : null,
                                meetingNote.content,
                              ].filter(Boolean).join(" · ")
                            }
                          />
                        )}
                        {a?.cautions && a.cautions.length > 0 && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                data-testid={`cell-caution-${emp.id}-${iso}`}
                                onClick={(e) => e.stopPropagation()}
                                className="absolute top-0.5 right-0.5 min-w-[14px] h-[14px] px-[3px] rounded-full bg-rose-500 text-white text-[9px] font-bold grid place-items-center shadow-sm leading-none ring-1 ring-white/70"
                                aria-label={`${a.cautions.length} caution${a.cautions.length === 1 ? "" : "s"}`}
                              >
                                {a.cautions.length > 1 ? a.cautions.length : <AlertTriangle size={8} strokeWidth={3} />}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-xs max-w-[280px] whitespace-normal">
                              <div className="font-semibold mb-1 flex items-center gap-1">
                                <AlertTriangle size={11} /> Caution
                              </div>
                              <ul className="space-y-1 list-disc pl-4 text-[11px] text-slate-100">
                                {a.cautions.map((msg, i) => (<li key={i}>{msg}</li>))}
                              </ul>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </button>
                    ) : covs.length > 0 ? (
                      // Coverage-only cell: no regular shift, only the coverage badge
                      <button
                        onClick={() => { if (canEdit) onCoverageCellClick?.(covs[0]); else onCellReveal?.(emp, iso, covs[0]); }}
                        data-testid={`cell-${emp.id}-${iso}`}
                        className={`cell-base relative m-1 h-12 lg:h-14 rounded-lg border border-pink-300 bg-pink-50 text-pink-800
                                    w-[calc(100%-8px)] text-[10px] lg:text-[11px] font-semibold flex flex-col items-center justify-center leading-tight
                                    ${canEdit ? "cursor-pointer hover:bg-pink-100 hover:border-pink-400" : "cursor-help"}`}
                      >
                        <span>{covs[0].code}Cov</span>
                        <span className="text-[9px] font-medium opacity-80 tabular-nums">
                          {covs.reduce((s, cv) => s + (cv.hours || 0), 0)}h
                        </span>
                      </button>
                    ) : (
                      (() => {
                        // Meeting notes on off-day cells → colored ring + time
                        // chip so the employee can see "you're attending a
                        // meeting from HH:MM–HH:MM today" without a shift.
                        const sn = standaloneNoteByCell.get(`${emp.id}|${iso}`);
                        const meetingNote = sn && (sn.kind || "").startsWith("meeting_") ? sn : null;
                        const meetingRingStyle = meetingNote
                          ? { boxShadow: `inset 0 0 0 2px ${meetingNote.color || "#2563eb"}` }
                          : null;
                        return (
                      <button
                        onClick={handleClick}
                        data-testid={`cell-${emp.id}-${iso}`}
                        style={meetingRingStyle || undefined}
                        title={
                          meetingNote
                            ? [
                                meetingNote.meeting_role === "cover" ? "Covering meeting" : "Attending meeting",
                                (meetingNote.meeting_start && meetingNote.meeting_end)
                                  ? `${meetingNote.meeting_start}–${meetingNote.meeting_end}`
                                  : null,
                                meetingNote.content,
                              ].filter(Boolean).join(" · ")
                            : undefined
                        }
                        className={`cell-base relative m-1 h-12 lg:h-14 rounded-lg border w-[calc(100%-8px)]
                                    ${hasPendingOfferForCell ? `${COLOR_MAP.offered_green.bg} ${COLOR_MAP.offered_green.text} ${COLOR_MAP.offered_green.border} ring-1 ring-emerald-300` : `${c.bg} ${c.text} ${c.border}`}
                                    text-xs lg:text-sm font-semibold flex flex-col items-center justify-center leading-tight
                                    ${canEdit ? "cursor-pointer hover:shadow-sm hover:border-blue-400" : "cursor-default"}
                                    ${isWeekend && !hasPendingOfferForCell ? "bg-slate-50" : ""}
                                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1`}
                      >
                        {hasPendingOfferForCell ? (
                          <>
                            <span className="text-[9px] uppercase tracking-widest font-black text-emerald-700">OFFER</span>
                            <span className="text-[10px] font-medium text-emerald-800 tabular-nums">{cellOffers.map(o => o.shift_code).join(" / ")}</span>
                          </>
                        ) : meetingNote ? (
                          <span
                            className="text-[10px] uppercase tracking-widest font-black leading-none"
                            style={{ color: meetingNote.color || "#2563eb" }}
                            data-testid={`cell-meeting-label-${emp.id}-${iso}`}
                          >
                            {meetingNote.meeting_role === "cover" ? "Cover" : "Mtg"}
                          </span>
                        ) : (
                          <span className="text-slate-300">·</span>
                        )}
                        {standaloneNoteByCell.has(`${emp.id}|${iso}`) && !meetingNote && (
                          <span
                            data-testid={`cell-note-${emp.id}-${iso}`}
                            className="absolute top-0.5 right-0.5 min-w-[14px] h-[14px] px-1 rounded-full bg-amber-500 text-white text-[9px] font-bold grid place-items-center shadow-sm leading-none tabular-nums"
                            aria-label="manager note"
                          >
                            <StickyNote size={8} strokeWidth={3} />
                          </span>
                        )}
                        {meetingNote && (
                          <span
                            className="absolute bottom-0 left-1 right-1 h-1 rounded-full"
                            style={{ background: meetingNote.color || "#2563eb" }}
                            title={meetingNote.content}
                          />
                        )}
                      </button>
                        );
                      })()
                    );

                    return (
                      <div
                        key={iso}
                        onPointerEnter={(e) => { if (e.pointerType !== "touch") setHoverTarget(iso, emp.id); }}
                        onPointerLeave={(e) => { if (e.pointerType !== "touch") clearHoverTarget(); }}
                        className={`flex ${isWeekend ? "col-weekend" : ""} ${isToday ? "border-l-2 border-r-2 border-blue-500 bg-blue-50/20" : ""} ${dividerByIso.get(iso) ? "!border-l-[3px] !border-l-slate-600" : ""}`}>
                        {(a || covs.length > 0 || hasPendingOfferForCell || standaloneNoteByCell.has(`${emp.id}|${iso}`) || nsoReqs.length > 0) ? (
                          <Tooltip>
                            <TooltipTrigger asChild>{cellButton}</TooltipTrigger>
                            <TooltipContent side="top" className="text-xs max-w-[280px]">
                              {!a && nsoReqs.length > 0 ? (
                                <div>
                                  <div className="font-medium">
                                    {nsoOtReview ? "NSO — OT-review request"
                                     : nsoBumped ? "NSO — request not assigned"
                                     : "NSO — pending request"}
                                  </div>
                                  <ul className="text-[11px] text-slate-100 mt-1 space-y-0.5">
                                    {nsoReqs.map((r, i) => (
                                      <li key={i}>
                                        <span className="font-mono">{r.shift_code}</span>
                                        {r.wants_ot ? " · wants OT" : ""}
                                        {r.rejection_reason ? ` · ${r.rejection_reason}` : ""}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ) : !a && hasPendingOfferForCell ? (
                                <div>
                                  <div className="font-medium">Pending shift offer</div>
                                  <div className="text-[11px] text-emerald-100 mt-0.5">
                                    Offered: {cellOffers.map(o => o.shift_code).join(", ")}
                                  </div>
                                  {(() => { const sn = standaloneNoteByCell.get(`${emp.id}|${iso}`); return sn?.content ? (
                                    <div className="text-[11px] text-amber-100 mt-1 whitespace-pre-wrap">📝 {sn.content}</div>
                                  ) : null; })()}
                                </div>
                              ) : !a && standaloneNoteByCell.has(`${emp.id}|${iso}`) ? (
                                <div>
                                  <div className="font-medium">Manager note</div>
                                  <div className="text-[11px] text-amber-100 mt-0.5 whitespace-pre-wrap">
                                    {standaloneNoteByCell.get(`${emp.id}|${iso}`).content}
                                  </div>
                                </div>
                              ) : isExternal ? (
                                <div>
                                  <div className="font-medium">Working at {a.external_facility_label}</div>
                                  <div className="text-[11px] text-slate-100 mt-0.5">
                                    {a.shift_code} · {a.hours || 0}h
                                  </div>
                                  <div className="text-[10px] text-slate-300 mt-1">
                                    Not counted toward this facility&apos;s hours.
                                  </div>
                                </div>
                              ) : a ? (
                                <>
                                  <div className="font-medium">
                                    {shift ? shiftLabel(shift) : a.shift_code}
                                    {isModified && !stackedExtras && <span className="ml-1 text-pink-200">· {a.start_time}–{a.end_time} · {a.hours}h</span>}
                                  </div>
                                  {stackedExtras && (
                                    <div className="mt-1 space-y-0.5 text-[11px]">
                                      <div className="text-slate-200">
                                        <span className="font-mono bg-white/10 px-1 rounded">{a.shift_code}</span>
                                        <span className="ml-1 tabular-nums">{a.start_time}–{a.end_time}</span>
                                      </div>
                                      {stackedExtras.map((ex, i) => (
                                        <div key={i} className="text-slate-200">
                                          <span className="font-mono bg-white/10 px-1 rounded">{ex.shift_code}</span>
                                          <span className="ml-1 tabular-nums">{ex.start_time}–{ex.end_time}</span>
                                          {ex.ot_hours > 0 && <span className="ml-1 text-red-300">· OT {ex.ot_hours}h</span>}
                                        </div>
                                      ))}
                                      <div className="text-slate-100 pt-0.5 border-t border-white/10 tabular-nums">
                                        Total {a.hours}h{a.ot_hours > 0 ? ` · ${a.ot_hours}h OT` : ""}
                                      </div>
                                    </div>
                                  )}
                                  {isModified && a.modified_note && (
                                    <div className="text-[11px] text-pink-100 mt-0.5 italic">{a.modified_note}</div>
                                  )}
                                  {a.covered_by_employee_name && (
                                    <div className="text-[11px] text-pink-100 mt-0.5">
                                      Covered by <b>{a.covered_by_employee_name}</b>
                                    </div>
                                  )}
                                  {hasNote && a.notes && a.notes.length > 0 && (
                                    <div className="mt-1 space-y-1">
                                      {a.notes.map((n) => (
                                        <div key={n.id} className="text-[11px] text-slate-100">
                                          <span className="font-semibold">({n.visibility})</span> {n.content}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </>
                              ) : covs.length > 0 ? (
                                <>
                                  <div className="font-medium">Covering {covs[0].source_employee_name}</div>
                                  <div className="text-[11px] text-slate-100 mt-0.5">
                                    {covs[0].code} · {covs[0].start_time && covs[0].end_time ? `${covs[0].start_time}–${covs[0].end_time} · ` : ""}{covs.reduce((s, cv) => s + (cv.hours || 0), 0)}h
                                  </div>
                                  {canEdit && <div className="text-[10px] text-pink-200 mt-1">Click to clear / reassign</div>}
                                </>
                              ) : null}
                            </TooltipContent>
                          </Tooltip>
                        ) : cellButton}
                      </div>
                    );
                  })}
                  <div className="p-2 flex items-center justify-center text-xs lg:text-sm tabular-nums text-slate-700 font-medium border-l border-slate-100">
                    {totalHoursFor(emp.id)}h
                  </div>
                </div>
                {availabilityByEmployee[emp.id] && availabilityByEmployee[emp.id].length > 0 && (
                  <div
                    key={`avail-${emp.id}`}
                    className={`grid border-b border-slate-100 bg-slate-50/60 ${gridColsLg}`}
                    style={{ gridTemplateColumns: gridCols }}
                    data-testid={`avail-row-${emp.id}`}
                  >
                    <div className="sticky left-0 z-10 bg-slate-50 border-r border-slate-100 px-2 py-1 flex items-center gap-1 text-[9px] uppercase tracking-widest text-slate-500 font-semibold shadow-[6px_0_10px_-8px_rgba(15,23,42,0.15)]">
                      Availability
                    </div>
                    {days.map((d) => {
                      const iso = format(d, "yyyy-MM-dd");
                      const rec = (availabilityByEmployee[emp.id] || []).find(x => x.date === iso);
                      const codes = rec?.codes || [];
                      const dv = dividerByIso.get(iso);
                      return (
                        <div key={iso} className={`border-l border-slate-100 py-1 flex items-center justify-center gap-0.5 ${dv ? "!border-l-[3px] !border-l-slate-600" : ""}`}>
                          {codes.length === 0 ? (
                            <span className="text-[9px] text-slate-300">·</span>
                          ) : codes.map((c) => (
                            <span
                              key={c}
                              className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[9px] font-bold ${
                                c === "D" ? "bg-amber-200 text-amber-900" :
                                c === "E" ? "bg-orange-200 text-orange-900" :
                                            "bg-indigo-200 text-indigo-900"
                              }`}
                              title={c === "D" ? "Days" : c === "E" ? "Evenings" : "Nights"}
                            >
                              {c}
                            </span>
                          ))}
                        </div>
                      );
                    })}
                    <div className="border-l border-slate-100" />
                  </div>
                )}
                </React.Fragment>
              );
            })}
            {employees.length === 0 && (
              <div className="p-10 text-center text-sm text-slate-500">No employees found.</div>
            )}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
